import React, { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CloudOff, Trash2, Check, Sparkles, Upload, FileText, FileJson } from 'lucide-react';
import { useJournal } from '../hooks/useJournal';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';

const BOTANICAL =
  'https://static.prod-images.emergentagent.com/jobs/e4a56433-de33-48f0-9e84-678bcf45ff81/images/66b640d47ffb68aa6564b4f20e079c0280ed4756cc3aec8b3de2b9b504cc868b.png';

const MOODS = [
  { id: 'calm', label: 'Calm', color: '#D8E2EE', emoji: '🌿' },
  { id: 'hopeful', label: 'Hopeful', color: '#F2DDE2', emoji: '✨' },
  { id: 'anxious', label: 'Anxious', color: '#F2E8D8', emoji: '🌧' },
  { id: 'sad', label: 'Sad', color: '#E6E0EE', emoji: '💧' },
  { id: 'angry', label: 'Angry', color: '#F4D9DC', emoji: '🔥' },
  { id: 'numb', label: 'Numb', color: '#E8E8EE', emoji: '⚪' },
];

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function downloadBlob(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

function todayStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function Journal() {
  const { entries, loading, addEntry, deleteEntry, replaceAll } = useJournal();
  const [content, setContent] = useState('');
  const [mood, setMood] = useState(null);
  const [importMsg, setImportMsg] = useState(null);
  const fileInputRef = useRef(null);

  const wordCount = useMemo(
    () => content.trim().split(/\s+/).filter(Boolean).length,
    [content],
  );

  const handleSave = () => {
    if (!content.trim()) return;
    addEntry({ content: content.trim(), mood });
    setContent('');
    setMood(null);
  };

  const handleDelete = (entry) => {
    if (window.confirm('Delete this entry? This cannot be undone.')) {
      deleteEntry(entry.id);
    }
  };

  const moodLabel = (id) => MOODS.find((m) => m.id === id)?.label || '—';

  const handleDownloadText = () => {
    if (entries.length === 0) return;
    const lines = [];
    lines.push('THE FULL IMPACT — JOURNAL EXPORT');
    lines.push(`Exported: ${new Date().toLocaleString('en-GB')}`);
    lines.push(`${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}`);
    lines.push('—'.repeat(60));
    lines.push('');
    // entries are newest-first; export oldest-first for natural reading
    [...entries]
      .slice()
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
      .forEach((e, i) => {
        const dt = new Date(e.created_at).toLocaleString('en-GB');
        lines.push(`#${i + 1}  ${dt}`);
        if (e.mood) lines.push(`Mood: ${moodLabel(e.mood)}`);
        lines.push('');
        lines.push(e.content);
        lines.push('');
        lines.push('—'.repeat(60));
        lines.push('');
      });
    downloadBlob(
      `the-full-impact-journal-${todayStamp()}.txt`,
      lines.join('\n'),
      'text/plain;charset=utf-8',
    );
  };

  const handleDownloadJson = () => {
    if (entries.length === 0) return;
    const backup = {
      app: 'The Full Impact',
      version: 1,
      exported_at: new Date().toISOString(),
      entries,
    };
    downloadBlob(
      `the-full-impact-journal-backup-${todayStamp()}.json`,
      JSON.stringify(backup, null, 2),
      'application/json',
    );
  };

  const handleRestoreClick = () => {
    setImportMsg(null);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const list = Array.isArray(parsed) ? parsed : parsed?.entries;
      if (!Array.isArray(list)) throw new Error('Not a valid journal backup file.');
      const replaceMsg =
        entries.length > 0
          ? `Replace your current ${entries.length} entry/entries with ${list.length} from the backup? Existing entries will be removed.`
          : `Import ${list.length} entries from the backup?`;
      if (!window.confirm(replaceMsg)) return;
      const n = replaceAll(list);
      setImportMsg(`Restored ${n} entr${n === 1 ? 'y' : 'ies'} from your backup file.`);
    } catch (err) {
      setImportMsg(
        `Sorry, that file could not be read as a journal backup. ${err?.message || ''}`.trim(),
      );
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="journal-screen">
      <SEO
        title="Private Journal — Anonymous & Browser-Only"
        description="A calm, private place to write. Anything you write stays in your browser — no account, no sign-up, no tracking."
        path="/app/journal"
      />
      <p className="eyebrow">PRIVATE JOURNAL</p>
      <h1
        className="font-heading mt-3"
        style={{ fontSize: 'clamp(28px, 4vw, 36px)', lineHeight: 1.15, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
      >
        A gentle place for your thoughts.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        Whatever you write here stays in this browser. Nothing is uploaded. Clearing your browser
        data will erase these entries.
      </p>

      <div className="mt-6 flex gap-2 flex-wrap">
        <PrintButton label="Print / Save your entries" />
        <button
          type="button"
          onClick={handleDownloadText}
          data-testid="journal-download-txt"
          disabled={entries.length === 0}
          className="tf-no-print inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-bold"
          style={{
            background: '#fff',
            color: 'var(--tf-purple)',
            border: '1px solid var(--tf-purple-light)',
            opacity: entries.length === 0 ? 0.5 : 1,
            cursor: entries.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          <FileText size={13} />
          Download as text
        </button>
        <button
          type="button"
          onClick={handleDownloadJson}
          data-testid="journal-download-json"
          disabled={entries.length === 0}
          className="tf-no-print inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-bold"
          style={{
            background: '#fff',
            color: 'var(--tf-purple)',
            border: '1px solid var(--tf-purple-light)',
            opacity: entries.length === 0 ? 0.5 : 1,
            cursor: entries.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          <FileJson size={13} />
          Download backup
        </button>
        <button
          type="button"
          onClick={handleRestoreClick}
          data-testid="journal-restore"
          className="tf-no-print inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-bold"
          style={{
            background: 'var(--tf-bg-secondary)',
            color: 'var(--tf-text-secondary)',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <Upload size={13} />
          Restore from file
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={handleFileChosen}
          style={{ display: 'none' }}
          data-testid="journal-restore-input"
        />
      </div>
      {importMsg && (
        <div
          className="mt-3 p-3 rounded-xl tf-no-print"
          style={{
            background: 'var(--tf-pink-soft)',
            color: 'var(--tf-text)',
            fontSize: 12.5,
          }}
          data-testid="journal-import-msg"
        >
          {importMsg}
        </div>
      )}

      {/* Guided journal CTA — three clear actions: read online, or grab a PDF */}
      <div
        data-testid="guided-journal-cta"
        className="mt-6 p-5 rounded-2xl tf-no-print"
        style={{
          background:
            'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
          color: '#fff',
          boxShadow: '0 10px 26px rgba(139,123,177,0.22)',
        }}
      >
        <p
          className="font-body font-bold"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.22em',
            color: 'rgba(255,255,255,0.92)',
          }}
        >
          NEW · GUIDED REFLECTION
        </p>
        <p
          className="font-heading mt-1.5"
          style={{
            fontSize: 19,
            color: '#fff',
            letterSpacing: '-0.01em',
            lineHeight: 1.25,
          }}
        >
          Work through a calm, step-by-step journal
        </p>
        <p
          className="font-body mt-2"
          style={{
            fontSize: 13,
            color: 'rgba(255,255,255,0.92)',
            lineHeight: 1.55,
          }}
        >
          Seven short prompts that remind you you&apos;re safe, that what happened was not your
          fault, and gently walk you through naming your feelings and finding one small act of
          self-kindness. Skip anything you&apos;re not ready for.
        </p>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Link
            to="/app/guided-journal"
            data-testid="journal-cta-online"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-bold"
            style={{
              background: '#fff',
              color: 'var(--tf-purple-dark)',
              textDecoration: 'none',
              fontSize: 13,
              flex: 1,
            }}
          >
            <Sparkles size={14} />
            Start online
          </Link>
          <a
            href="https://customer-assets.emergentagent.com/job_justice-pathway-uk/artifacts/lq2f708l_The%20Full%20Impact%20Campaign%20%E2%80%94%20Guided%20Journal%20Workbook%20Colour.pdf%20%282%29.pdf"
            target="_blank"
            rel="noopener noreferrer"
            download
            data-testid="journal-cta-pdf-colour"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-bold"
            style={{
              background: 'rgba(255,255,255,0.18)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.5)',
              textDecoration: 'none',
              fontSize: 13,
              flex: 1,
            }}
          >
            <FileText size={14} />
            PDF — Colour
          </a>
          <a
            href="https://customer-assets.emergentagent.com/job_justice-pathway-uk/artifacts/ieg473n4_The%20Full%20Impact%20Campaign%20%E2%80%94%20Guided%20Journal%20Workbook%20Simple%20Black%20and%20White.pdf.pdf"
            target="_blank"
            rel="noopener noreferrer"
            download
            data-testid="journal-cta-pdf-bw"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-bold"
            style={{
              background: 'rgba(255,255,255,0.18)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.5)',
              textDecoration: 'none',
              fontSize: 13,
              flex: 1,
            }}
          >
            <FileText size={14} />
            PDF — Black &amp; White
          </a>
        </div>
        <p
          className="font-body mt-3"
          style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.82)', lineHeight: 1.5 }}
        >
          The PDFs open in a new tab. From there your browser&apos;s share / download menu
          will save them to your device. Print at home, or take a copy to your next ISVA,
          counselling or support session.
        </p>
      </div>

      {/* Privacy card */}
      <div
        className="mt-6 rounded-2xl p-4 flex items-center gap-4"
        style={{ background: 'var(--tf-bg-secondary)', border: '1px solid var(--tf-card-border)' }}
        data-testid="privacy-card"
      >
        <img
          src={BOTANICAL}
          alt=""
          aria-hidden
          className="w-10 h-10 object-contain"
          style={{ opacity: 0.85 }}
        />
        <div className="flex-1 min-w-0">
          <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
            Stored only in your browser
          </p>
          <p
            className="font-body"
            style={{ fontSize: 11.5, color: 'var(--tf-text-secondary)', marginTop: 2 }}
          >
            Anonymous mode · No sign-up · No cloud backup
          </p>
        </div>
        <div
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{ background: '#fff', color: 'var(--tf-purple)' }}
        >
          <CloudOff size={13} />
          <span className="font-body font-bold" style={{ fontSize: 11.5 }}>
            Offline only
          </span>
        </div>
      </div>

      {/* Composer */}
      <div className="tf-card mt-6 p-6 tf-no-print">
        <p
          className="font-body font-bold"
          style={{ fontSize: 13, color: 'var(--tf-text)', marginBottom: 12 }}
        >
          How are you feeling?
        </p>
        <div className="flex flex-wrap gap-2">
          {MOODS.map((m) => {
            const selected = mood === m.id;
            return (
              <button
                key={m.id}
                type="button"
                data-testid={`mood-${m.id}`}
                onClick={() => setMood(selected ? null : m.id)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full transition-all"
                style={{
                  background: m.color,
                  border: `1.5px solid ${selected ? 'var(--tf-purple)' : 'transparent'}`,
                  transform: selected ? 'scale(1.02)' : 'scale(1)',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 15 }}>{m.emoji}</span>
                <span
                  className="font-body font-bold"
                  style={{ fontSize: 12.5, color: 'var(--tf-text)' }}
                >
                  {m.label}
                </span>
              </button>
            );
          })}
        </div>

        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          data-testid="journal-input"
          placeholder="Write whatever comes to mind. There is no right or wrong way to do this."
          rows={8}
          className="w-full mt-5 p-4 rounded-xl outline-none font-body"
          style={{
            background: 'var(--tf-bg)',
            border: '1px solid var(--tf-card-border)',
            fontSize: 15,
            lineHeight: 1.6,
            color: 'var(--tf-text)',
            resize: 'vertical',
            minHeight: 160,
          }}
        />

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <span
            className="font-body"
            style={{ fontSize: 12, color: 'var(--tf-text-muted)' }}
          >
            {wordCount} word{wordCount === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={!content.trim()}
            data-testid="journal-save-button"
            className="tf-btn-primary"
            style={{
              opacity: !content.trim() ? 0.5 : 1,
              cursor: !content.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            <Check size={16} /> Save entry
          </button>
        </div>
      </div>

      {/* List */}
      <p
        className="font-body font-bold mt-10 mb-4"
        style={{ fontSize: 11.5, letterSpacing: '0.18em', color: 'var(--tf-text-muted)' }}
      >
        {entries.length === 0 ? 'YOUR ENTRIES WILL APPEAR HERE' : `${entries.length} ENTR${entries.length === 1 ? 'Y' : 'IES'}`}
      </p>

      {loading && (
        <p className="font-body" style={{ color: 'var(--tf-text-secondary)' }}>
          Loading…
        </p>
      )}

      {!loading && entries.length === 0 && (
        <div
          className="p-8 rounded-2xl text-center flex flex-col items-center gap-3"
          style={{
            background: 'var(--tf-bg-secondary)',
            border: '1px dashed var(--tf-card-border)',
          }}
          data-testid="journal-empty"
        >
          <img src={BOTANICAL} alt="" aria-hidden className="w-14 h-14 object-contain opacity-70" />
          <p
            className="font-body max-w-xs"
            style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
          >
            Whatever you write is just for you. Take your time.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {entries.map((entry) => {
          const moodMeta = MOODS.find((m) => m.id === entry.mood);
          return (
            <div key={entry.id} className="tf-card p-5" data-testid={`entry-${entry.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {moodMeta && (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ background: moodMeta.color }}
                    >
                      <span style={{ fontSize: 14 }}>{moodMeta.emoji}</span>
                    </div>
                  )}
                  <span
                    className="font-body"
                    style={{ fontSize: 12, color: 'var(--tf-text-secondary)' }}
                  >
                    {formatDate(entry.created_at)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(entry)}
                  data-testid={`delete-${entry.id}`}
                  aria-label="Delete entry"
                  className="p-2 rounded-full"
                  style={{ background: 'transparent', color: 'var(--tf-text-muted)' }}
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <p
                className="font-body whitespace-pre-wrap"
                style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--tf-text)' }}
              >
                {entry.content}
              </p>
            </div>
          );
        })}
      </div>

      <div
        className="mt-10 p-5 rounded-2xl flex items-start gap-3"
        style={{ background: 'var(--tf-pink-soft)' }}
      >
        <Sparkles size={18} color="var(--tf-purple-dark)" />
        <div>
          <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
            Journaling can be hard.
          </p>
          <p
            className="font-body"
            style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', marginTop: 4, lineHeight: 1.6 }}
          >
            If something brought up a strong feeling, try a grounding exercise — there&apos;s a
            shortcut at the bottom of the screen.
          </p>
        </div>
      </div>
    </div>
  );
}
